<template>
  <div
    ref="careerSearchRef"
    class="i-career-search"
  >
    <input
      v-model="data.searchKey"
      class="input"
      :class="{ dropdown: data.searchDropdownTriggered }"
      type="text"
      placeholder="Search for a career"
      @focus="handleSearchFocus"
    />
    <div
      v-if="data.searchDropdownTriggered"
      class="jobs"
    >
      <template v-if="data.jobsFound.length">
        <div
          v-for="job in data.jobsFound"
          :key="job.id"
          class="job"
          @click.stop="handleCareerClick(job.id)"
        >
          {{ job.jobName }}
        </div>
      </template>
      <img
        v-else
        class="spinner"
        :src="iconSpinner"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { useRouter } from "vue-router"
import { debounce } from "lodash"

import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"

// ASSETS
import iconSpinner from "@/assets/images/spinner.gif"

//TYPES
import { MatchedJob } from "@/shared/dtos/out/JobGetMatchedOut"

// ROUTERS
const router = useRouter()

// REFS AND DATA
const careerSearchRef = ref<HTMLElement | null>(null)
const data = reactive({
  searchKey: "",
  jobsFound: [] as MatchedJob[],
  searchDropdownTriggered: false
})

// FUNCTIONS
const searchForCareer = async (searchKey: string) => {
  try {
    const response = await api.job.getJobMatches({ searchKey, limit: 40 })
    data.jobsFound = response.jobs
  } catch (error) {
    console.error(`Failed to fetch careers matching '${searchKey}'`, error)
  }
}

const debouncedSearch = debounce((key: string) => {
  searchForCareer(key)
}, 500)

const handleCareerClick = (jobId: number) => {
  router.push({
    name: GlobalVariables.urls.report.children.careers.routeName,
    params: {
      jobId
    }
  })
}

const handleClickOutside = (event: MouseEvent) => {
  if (careerSearchRef.value && !careerSearchRef.value.contains(event.target as Node)) {
    data.jobsFound = []
    data.searchDropdownTriggered = false
  }
}

const handleSearchFocus = () => {
  if (data.searchKey.trim().length >= 3) {
    data.searchDropdownTriggered = true
    debouncedSearch(data.searchKey.trim())
  }
}

// LIFECYCLE HOOKS
watch(
  () => data.searchKey,
  (newQuery) => {
    data.searchDropdownTriggered = true
    const key = newQuery.trim()
    if (key.length >= 3) debouncedSearch(key)
    if (key.length < 3) data.jobsFound = []
    if (key.length == 0) data.searchDropdownTriggered = false
  }
)

onMounted(() => {
  document.addEventListener("click", handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside)
})
</script>

<style lang="scss" scoped>
.i-career-search {
  @include flex(column, start, center);

  width: 100%;
  max-width: 600px;
  position: relative;

  .input {
    @include border-radius-sm;
    @include text-md;

    border: 1px solid $zumthor200;
    color: $black600;
    min-height: 40px;
    outline: none;
    padding: 5px 10px;
    width: 100%;

    &.dropdown {
      border-radius: 8px 8px 0 0;
      border-bottom: 2px solid $zumthor200;
    }
  }

  .jobs {
    @include flex(column, start, center);
    @include text-sm;

    position: absolute;
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
    border: 1px solid $zumthor200;
    border-top: none;
    width: 100%;
    max-height: 270px;
    overflow-y: auto;
    background-color: $white;
    top: 40px;

    @media screen and (min-width: $breakpoint-sm) {
      @include text-md;
    }

    // scrollbar styling
    &::-webkit-scrollbar {
      height: 5px;
      width: 5px;
      background: $black50;
    }

    &::-webkit-scrollbar-thumb {
      background: $black200;
      border-radius: 10px;
    }

    .job {
      width: 100%;
      padding: 10px;
      cursor: pointer;

      &:not(:last-child) {
        border-bottom: 1px solid $zumthor200;
      }

      &:hover {
        background-color: $azure-radiance50;
      }
    }
  }

  .spinner {
    margin: 10px;
    width: 25px;
  }
}
</style>
